import { openAlert } from './alertActions';
import { login } from './userActions';
import { postSignup, postResetPassword, postVerifyCode, putPassword } from '~/api/signUp.js';
import { mixpanel } from '~/third_party/Mixpanel';
import { googleTagManager } from '~/third_party/GoogleTagManager';
import { fbEvents } from '~/third_party/Facebook';
import { AppsflyerPBA } from '~/third_party/Appsflyer';

const BASE = 'signup :';

export const signUpActionTypes = {
  signUpSuccess: `${BASE} SUCCESS`,
  signUpFailed: `${BASE} FAILED`,

  sentCodeStart: `${BASE} send code : persist start`,
  sentCodeSuccess: `${BASE} send code : persist SUCCESS`,
  sentCodeFailed: `${BASE} send code : persist FAILED`,

  verifyCodeStart: `${BASE} verify code : persist start`,
  verifyCodeSuccess: `${BASE} verify code : persist SUCCESS`,
  verifyCodeFailed: `${BASE} verify code : persist FAILED`,
};

/**
 *
 * @param {object} params
 * @param {string} params.email
 * @param {string} params.firstName
 * @param {string} params.lastName
 * @param {string} [params.password]
 * @param {string} [params.phoneNumber]
 * @param {string} [params.provider]
 * @param {string} [params.providerUid]
 * @param {string} [params.providerToken]
 * @param {function} [params.signUpCallback]
 */
export const signUp = ({
  email,
  firstName,
  lastName,
  password,
  phoneNumber,
  provider,
  providerUid,
  providerToken,
  signUpCallback,
}) => async (dispatch, getState, { intl }) => {
  mixpanel.track({ name: 'sign-up : sign-up-submitted', properties: { method: provider } });
  googleTagManager.trackSignup(provider);
  fbEvents.trackWebSignUp(provider);

  const state = getState();
  const { formatMessage } = intl(state);
  try {
    const data = await postSignup({
      email,
      firstName,
      lastName,
      password,
      phoneNumber,
      provider,
      providerUid,
      providerToken,
    });
    if (data) {
      // third party sign up success
      mixpanel.alias(data.id);
    }
    mixpanel.track({ name: 'sign-up : sign-up-success', properties: { method: provider } });
    //facebook web signup event

    // then login to get the token
    await dispatch(login({ email, password, provider, providerUid, providerToken }));
    dispatch(signUpSuccess(data));
    dispatch(
      openAlert({
        message: formatMessage({ id: 'actions.signup.success' }),
        variant: 'success',
      })
    );
    AppsflyerPBA.trackWebCompleteRegister(provider);
    signUpCallback && signUpCallback();

  } catch (e) {
    mixpanel.track({ name: 'sign-up : sign-up-failed', properties: { method: provider } });
    dispatch(signUpFailed(e));
    let errorMessage = formatMessage({ id: 'actions.signup.failed' });
    if (e.errors) {
      errorMessage = e.errors;
    }
    if (e.response && e.response.data && e.response.data.errors) {
      const {
        data: { errors },
      } = e.response;
      if (typeof errors === 'string') {
        errorMessage = errors;
      } else if (
        errors.phone_number &&
        errors.phone_number.find((error) => error === 'is invalid')
      ) {
        errorMessage = formatMessage({ id: 'actions.signup.phone_number.invalid' });
      } else if (errors.email && errors.email.find((error) => error === 'is invalid')) {
        errorMessage = formatMessage({ id: 'actions.signup.email.invalid' });
      } else {
        errorMessage = formatMessage({ id: 'actions.signup.email_phone.taken' });
      }
    }
    dispatch(
      openAlert({
        message: errorMessage,
        variant: 'error',
      })
    );
  }
};

const signUpSuccess = (data) => {
  const { id } = data;
  mixpanel.alias(id);
  return {
    type: signUpActionTypes.signUpSuccess,
    data,
  };
};

const signUpFailed = (errors) => ({
  type: signUpActionTypes.signUpFailed,
  data: errors,
});

export const sendCode = ({ email }) => async (dispatch) => {
  try {
    dispatch({
      type: signUpActionTypes.sentCodeStart,
    });
    const message = await postResetPassword({ email });

    dispatch(
      openAlert({
        message,
        variant: 'success',
      })
    );
    dispatch({
      type: signUpActionTypes.sentCodeSuccess,
      email,
    });
  } catch ({ errors }) {
    const message = errors;
    dispatch(
      openAlert({
        message: message || 'Failed to send code.',
        variant: 'error',
      })
    );
    dispatch({
      type: signUpActionTypes.sentCodeFailed,
    });
  }
};

export const verifyCode = ({ email, code }) => async (dispatch) => {
  dispatch({
    type: signUpActionTypes.verifyCodeStart,
  });
  try {
    await postVerifyCode({ email, code });
    dispatch({
      type: signUpActionTypes.verifyCodeSuccess,
      verifyCode: code,
    });
  } catch ({ response }) {
    dispatch({
      type: signUpActionTypes.verifyCodeFailed,
    });
    const message = response && response.data.errors;
    dispatch(
      openAlert({
        message: message || 'Can not verify code.',
        variant: 'error',
      })
    );
  }
};

export const updatePassword = ({ verificationCode, email, password, router }) => async (
  dispatch
) => {
  try {
    await putPassword({ email, password, verificationCode });
    router.replace('/sign-in');
  } catch (e) {
    dispatch(
      openAlert({
        message: 'Failed to update password.',
        variant: 'error',
      })
    );
  }
};
